import { Grid } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { FC, useContext, useRef } from 'react';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import canadaProvinces from 'ui-kit/inputs/geo-data/canada-provinces.json';
import mexicoStates from 'ui-kit/inputs/geo-data/mexico-states.json';
import foreignTerritories from 'ui-kit/inputs/geo-data/foreign-territory.json';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import states from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { ValidationState } from '../../../types';
import { isUS, requiresStateAndPostalCode, isUSTerritory } from '@agentnet/utils';
import { FincenReportContext } from 'hooks/FincenReportContext';

interface ExtendedAddressFields extends AddressFields {
  addressType?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    padding: theme.spacing(2, 0, 0, 0),
    paddingRight: theme.spacing(2),
    '&:nth-child(2), &:last-child': {
      paddingRight: 0,
    },
  },
  fieldGroup: {
    marginBottom: theme.spacing(2),
  },
}));

interface AddressBlockProps {
  address: ExtendedAddressFields;
  onChange: (address: ExtendedAddressFields) => void;
  validation: ValidationState;
  isUSAddress: boolean;
  showStateLabel?: boolean;
  fieldPrefix?: string;
  clearFieldError?: (fieldPath: string) => void;
  isRequired?: boolean;
  transferorIndex?: number;
  trusteeIndex?: number;
}

const AddressBlock: FC<AddressBlockProps> = ({
  address,
  onChange,
  validation,
  showStateLabel = true,
  fieldPrefix,
  clearFieldError,
  isRequired = false,
  transferorIndex,
  trusteeIndex,
}) => {
  // Derive address type suffix from fieldPrefix for unique data-testid
  const getAddressTypeSuffix = () => {
    if (!fieldPrefix) return '';
    if (fieldPrefix.includes('foreignBusinessAddress')) return '-foreign-business';
    if (fieldPrefix.includes('foreignPrincipalAddress')) return '-foreign-principal';
    if (fieldPrefix.includes('primaryUSAddress')) return '-primary-us';
    return '';
  };

  // Generate index suffix for data-testid based on provided indices
  const getTestIdPrefix = () => {
    const addressTypeSuffix = getAddressTypeSuffix();
    if (transferorIndex !== undefined && trusteeIndex !== undefined) {
      return `fincen-trustee-${transferorIndex}-${trusteeIndex}-address${addressTypeSuffix}`;
    } else if (transferorIndex !== undefined) {
      return `fincen-transferor-${transferorIndex}-address${addressTypeSuffix}`;
    }
    return `fincen-address${addressTypeSuffix}`;
  };
  const testIdPrefix = getTestIdPrefix();
  const classes = useStyles();
  const fincenReportCtx = useContext(FincenReportContext);
  const { generateButtonClicked } = fincenReportCtx ?? {};

  // Track recently edited fields to suppress stale validation errors
  const recentlyEditedFieldsRef = useRef<Set<string>>(new Set());

  const shouldRequireStateAndPostalCode = requiresStateAndPostalCode(address.country);

  // Merge all territories for country dropdown and remove duplicates
  const allCountryOptions = [
    ...territories.map((t) => ({ name: t.name, value: t.abbreviation })),
    ...foreignTerritories.map((t) => ({ name: t.name, value: t.abbreviation })),
  ]
    .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value))
    .sort((a, b) => {
      // Define priority order
      const priorityOrder = [
        'United States',
        'American Samoa',
        'Guam',
        'Northern Mariana Islands',
        'Puerto Rico',
        'U.S. Virgin Islands',
        'Canada',
        'Mexico',
      ];

      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);

      // If both are in priority list, sort by their position
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      // If only a is in priority list, it comes first
      if (aIndex !== -1) return -1;
      // If only b is in priority list, it comes first
      if (bIndex !== -1) return 1;
      // Otherwise sort alphabetically
      return a.name.localeCompare(b.name);
    });

  const territoryOptions = territories.map((territory) => ({
    name: territory.name,
    value: territory.abbreviation,
  }));

  const stateOptions = states.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  const canadaProvinceOptions = canadaProvinces.map((province) => ({
    name: province.abbreviation,
    value: province.abbreviation,
  }));

  const mexicoStateOptions = mexicoStates.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  // Determine which state/province options to show based on country
  const getStateOptions = () => {
    const country = address.country?.toUpperCase();
    if (country === 'US') {
      return stateOptions;
    } else if (isUSTerritory(country)) {
      return territoryOptions.filter((t) => t.value === country);
    } else if (country === 'CA') {
      return canadaProvinceOptions;
    } else if (country === 'MX') {
      return mexicoStateOptions;
    }
    return [];
  };

  const getStateLabel = () => {
    const country = address.country?.toUpperCase();
    if (country === 'US' || isUSTerritory(country)) {
      return 'State/U.S. Territory';
    } else if (country === 'CA') {
      return 'Province/Territory';
    } else if (country === 'MX') {
      return 'State';
    }
    return 'State/Province';
  };

  const getFieldErrors = (fieldName: string) => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    // If this field was recently edited, suppress errors to prevent flash
    const fullFieldPath = fieldPrefix ? `${fieldPrefix}.${fieldName}` : fieldName;
    if (recentlyEditedFieldsRef.current.has(fullFieldPath)) {
      return undefined;
    }

    const error = validation.errors.find((e) => e.field === fullFieldPath);

    // Don't show "required" errors for fields that currently have values
    // This handles stale validation errors from context that hasn't been updated yet
    if (error && error.message?.toLowerCase().includes('required')) {
      const fieldValue = address[fieldName as keyof ExtendedAddressFields];
      if (fieldValue && String(fieldValue).trim() !== '') {
        return undefined;
      }
    }

    return error;
  };

  // Mark fields as recently edited to suppress stale validation errors
  const markFieldAsEdited = (fieldPath: string) => {
    recentlyEditedFieldsRef.current.add(fieldPath);
    // Clear the suppression after a short delay to allow validation to catch up
    setTimeout(() => {
      recentlyEditedFieldsRef.current.delete(fieldPath);
    }, 500);
  };

  const handleFieldChange = (updatedAddress: ExtendedAddressFields, changedField: keyof ExtendedAddressFields) => {
    // Only clear error if the field value actually changed
    const hasChanged = address[changedField] !== updatedAddress[changedField];

    // Clear the error for this specific field BEFORE updating state
    if (hasChanged && clearFieldError) {
      const fullFieldPath = fieldPrefix ? `${fieldPrefix}.${changedField}` : changedField;
      markFieldAsEdited(fullFieldPath);
      clearFieldError(fullFieldPath);
    }

    onChange(updatedAddress);
  };

  const handleChange = (field: keyof ExtendedAddressFields) => (eventOrValue: any) => {
    const value = eventOrValue && (eventOrValue.target ? eventOrValue.target.value : eventOrValue);

    // Check if value actually changed before doing anything
    if (address[field] === value) {
      return;
    }

    const newAddress = { ...address, [field]: value as string };

    if (field === 'country') {
      const countryUpper = value?.toUpperCase();
      // Auto-set state to territory value for US Territories, reset for other countries
      newAddress.state = isUSTerritory(countryUpper) ? countryUpper : '';
      newAddress.postalCode = '';

      if ('stateOrProvince' in newAddress) {
        newAddress.stateOrProvince = '';
      }

      // Mark fields as edited and clear errors BEFORE onChange to prevent flash
      const countryFieldPath = fieldPrefix ? `${fieldPrefix}.country` : 'country';
      const stateFieldPath = fieldPrefix ? `${fieldPrefix}.state` : 'state';
      const postalCodeFieldPath = fieldPrefix ? `${fieldPrefix}.postalCode` : 'postalCode';

      markFieldAsEdited(countryFieldPath);
      markFieldAsEdited(stateFieldPath);
      markFieldAsEdited(postalCodeFieldPath);

      if (clearFieldError) {
        clearFieldError(countryFieldPath);
        clearFieldError(stateFieldPath);
        clearFieldError(postalCodeFieldPath);
      }

      onChange(newAddress);
    } else {
      handleFieldChange(newAddress, field);
    }
  };

  return (
    <Grid container>
      <Grid item xs={6} className={classes.section}>
        <GoogleAutocompleteSearch
          label="Address 1"
          variant="outlined"
          required={isRequired}
          value={address.address1 || ''}
          onChange={handleChange('address1')}
          name={fieldPrefix ? `${fieldPrefix}.address1` : 'address1'}
          errs={validation.errors}
          showValidation={!!getFieldErrors('address1')}
          stateConstraint={isUS(address.country) && address.state ? address.state : undefined}
          onPlaceSelect={(place: any) => {
            // Use the place's country to determine if it's a US address, not the current form value
            const placeCountry = place?.country || address.country;
            const isUSPlace = isUS(placeCountry);
            const updated: ExtendedAddressFields = {
              ...address,
              address1: place?.address1 ?? '',
              address2: place?.address2 || address.address2 || '',
              city: place?.city ?? '',
              postalCode: place?.zipcode ?? '',
              country: placeCountry,
              // For US addresses, use the state from Google Places; for non-US, use the country as state
              state: isUSPlace ? place?.state ?? '' : placeCountry,
            };

            // Clear errors for all auto-filled fields
            if (clearFieldError) {
              const fieldsToUpdate = ['address1', 'address2', 'city', 'postalCode', 'country', 'state'];
              fieldsToUpdate.forEach((field) => {
                const fullFieldPath = fieldPrefix ? `${fieldPrefix}.${field}` : field;
                clearFieldError(fullFieldPath);
              });
            }

            onChange(updated);
          }}
          data-testid={`${testIdPrefix}-line1-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} className={classes.section}>
        <AgentNetTextInput
          label="Address 2"
          value={address.address2 || ''}
          onChange={handleChange('address2')}
          error={!!getFieldErrors('address2')}
          helperText={getFieldErrors('address2')?.message}
          data-testid={`${testIdPrefix}-line2-input`}
          fullWidth
        />
      </Grid>
      <Grid item xs={showStateLabel ? 12 : 4} md={showStateLabel ? 3 : 4} className={classes.section}>
        <AgentNetTextInput
          label="City"
          required={isRequired}
          variant="outlined"
          value={address.city || ''}
          onChange={handleChange('city')}
          name={fieldPrefix ? `${fieldPrefix}.city` : 'city'}
          error={!!getFieldErrors('city')}
          helperText={getFieldErrors('city')?.message}
          data-testid={`${testIdPrefix}-city-input`}
          fullWidth
        />
      </Grid>

      <Grid item xs={showStateLabel ? 12 : 4} md={showStateLabel ? 3 : 4} className={classes.section}>
        <AgentNetDropdownSelector
          label="Country/Jurisdiction"
          variant="outlined"
          required={isRequired}
          value={address.country || ''}
          menuOption={(value) => handleChange('country')(value)}
          name={fieldPrefix ? `${fieldPrefix}.country` : 'country'}
          error={!!getFieldErrors('country')}
          errs={getFieldErrors('country') ? [getFieldErrors('country')!] : undefined}
          showValidation={!!getFieldErrors('country')}
          options={allCountryOptions}
          data-testid={`${testIdPrefix}-country-select`}
          defaultToFirst={false}
          fullWidth
        />
      </Grid>
      {showStateLabel && shouldRequireStateAndPostalCode && (
        <Grid item xs={12} md={3} className={classes.section}>
          <AgentNetDropdownSelector
            label={getStateLabel()}
            variant="outlined"
            required={isRequired}
            value={address.state || ''}
            menuOption={(value) => handleChange('state')(value)}
            name={fieldPrefix ? `${fieldPrefix}.state` : 'state'}
            error={!!getFieldErrors('state')}
            errs={getFieldErrors('state') ? [getFieldErrors('state')!] : undefined}
            showValidation={!!getFieldErrors('state')}
            options={getStateOptions()}
            data-testid={`${testIdPrefix}-state-select`}
            defaultToFirst={false}
            fullWidth
            disabled={isUSTerritory(address.country?.toUpperCase())}
          />
        </Grid>
      )}
      <Grid item xs={showStateLabel ? 12 : 4} md={showStateLabel ? 3 : 4} className={classes.section}>
        {isUS(address.country) || isUSTerritory(address.country) ? (
          <USPostalCodeField
            label="Zip/Postal Code"
            variant="outlined"
            required={isRequired && shouldRequireStateAndPostalCode}
            value={address.postalCode || ''}
            onChange={handleChange('postalCode')}
            name={fieldPrefix ? `${fieldPrefix}.postalCode` : 'postalCode'}
            error={!!getFieldErrors('postalCode')}
            helperText={getFieldErrors('postalCode')?.message}
            data-testid={`${testIdPrefix}-postal-code-input`}
            fullWidth
          />
        ) : (
          <AgentNetTextInput
            label="Zip/Postal Code"
            variant="outlined"
            required={isRequired && shouldRequireStateAndPostalCode}
            value={address.postalCode || ''}
            onChange={handleChange('postalCode')}
            name={fieldPrefix ? `${fieldPrefix}.postalCode` : 'postalCode'}
            error={!!getFieldErrors('postalCode')}
            helperText={getFieldErrors('postalCode')?.message}
            data-testid={`${testIdPrefix}-postal-code-input`}
            fullWidth
          />
        )}
      </Grid>
    </Grid>
  );
};

export default AddressBlock;
